import { useState } from 'react'
import { useSupplier, useUpdateSupplier } from '@/hooks/useSuppliers'
import { SupplierEditForm } from './SupplierEditForm'
import type { UpdateSupplierData } from '@/types'

interface SupplierDetailProps {
  supplierId: string
  onBack: () => void
}

export function SupplierDetail({ supplierId, onBack }: SupplierDetailProps) {
  const { data: supplier, isLoading, error } = useSupplier(supplierId)
  const updateMutation = useUpdateSupplier()
  const [isEditing, setIsEditing] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const handleSave = (data: UpdateSupplierData) => {
    updateMutation.mutate(
      { id: supplierId, data },
      {
        onSuccess: () => {
          setIsEditing(false)
          setSaveSuccess(true)
          setTimeout(() => setSaveSuccess(false), 3000)
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="p-6" data-testid="detail-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    )
  }

  if (error || !supplier) {
    return (
      <div className="p-6" data-testid="detail-error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load supplier</p>
          <button
            onClick={onBack}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6" data-testid="supplier-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700"
            data-testid="back-btn"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h2 className="text-xl font-semibold text-gray-900">{supplier.name}</h2>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            data-testid="edit-btn"
          >
            Edit
          </button>
        )}
      </div>

      {/* Success message */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="success-message">
          <p className="text-green-800">Supplier updated successfully</p>
        </div>
      )}

      {/* Error message */}
      {updateMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-message">
          <p className="text-red-800">Failed to update supplier</p>
        </div>
      )}

      {/* Main content */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {isEditing ? (
          <SupplierEditForm
            supplier={supplier}
            onSave={handleSave}
            onCancel={() => setIsEditing(false)}
            isSaving={updateMutation.isPending}
          />
        ) : (
          <div className="space-y-6" data-testid="supplier-view">
            {/* Name */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Supplier Name</h3>
              <p className="mt-1 text-lg text-gray-900">{supplier.name}</p>
            </div>

            {/* Alternative Names */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Alternative Names</h3>
              {supplier.alternative_names?.length ? (
                <ul className="mt-2 space-y-1">
                  {supplier.alternative_names.map((name, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md"
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-gray-400">No alternative names</p>
              )}
            </div>

            {/* Metadata */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Metadata</h3>
              {supplier.metadata && Object.keys(supplier.metadata).length > 0 ? (
                <pre className="mt-2 p-3 bg-gray-50 rounded-md text-sm font-mono overflow-x-auto">
                  {JSON.stringify(supplier.metadata, null, 2)}
                </pre>
              ) : (
                <p className="mt-1 text-sm text-gray-400">No metadata</p>
              )}
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Created</h3>
                <p className="mt-1 text-sm text-gray-700">
                  {supplier.created_at
                    ? new Date(supplier.created_at).toLocaleString()
                    : '—'}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                <p className="mt-1 text-sm text-gray-700">
                  {supplier.updated_at
                    ? new Date(supplier.updated_at).toLocaleString()
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
